// PortalShowcase — "Built for every side" cards | Students / Counselors / Institutions | anchor targets for nav tabs | floating animation + hover glow | whileInView stagger reveal

import React, { useState, useEffect } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { colors, motion as motionTokens } from './landingTokens'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { CALENDLY_URL } from '../../constants'

export type PortalKey = 'student' | 'counselor' | 'college'

// How long the nav-requested card stays emphasized
const HIGHLIGHT_MS = 2000

// ──── Portal data ────────────────────────────────────────────

interface PortalData {
  label: string
  headline: string
  body: string
  /** Positive-gain outcome lines — what the buyer gets, never framed from absence */
  outcomes: string[]
  ctaText: string
  cardBg: string
  topBorder: string
  shadowBase: string
  shadowHover: string
  borderHover: string
  pillColor: string
  pillBorder: string
  btnColor: string
  btnBorder: string
  comingSoon?: boolean
}

const PORTAL_DATA: Record<'student' | 'counselor' | 'college', PortalData> = {
  student: {
    label: 'Students',
    headline: 'Work with your counselor in one place.',
    body: 'Essays, deadlines, and your college list — with AI help at every step.',
    outcomes: [
      'Everything in one place — essays, deadlines, and your list',
      'You always know what is due next',
      'Your counselor works alongside you, not in a separate inbox',
    ],
    ctaText: 'Book a Free Intro Call →',
    cardBg: 'linear-gradient(135deg, rgba(168,85,247,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    topBorder: 'rgba(168,85,247,0.5)',
    shadowBase: 'rgba(168,85,247,0.08)',
    shadowHover: 'rgba(168,85,247,0.18)',
    borderHover: 'rgba(255,255,255,0.14)',
    pillColor: 'rgba(168,85,247,0.9)',
    pillBorder: 'rgba(168,85,247,0.3)',
    btnColor: 'rgba(168,85,247,0.9)',
    btnBorder: 'rgba(168,85,247,0.4)',
  },
  counselor: {
    label: 'Counselors',
    headline: 'Run every student from one dashboard.',
    body: "See who's on track, who's behind, and what needs review — before deadlines hit.",
    outcomes: [
      'Your whole caseload in a single view',
      'You see who is behind while there is still time',
      'One place to work, instead of rebuilding the picture across tools',
    ],
    ctaText: 'Book a Demo →',
    cardBg: 'linear-gradient(135deg, rgba(236,72,153,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    topBorder: 'rgba(236,72,153,0.5)',
    shadowBase: 'rgba(236,72,153,0.08)',
    shadowHover: 'rgba(236,72,153,0.18)',
    borderHover: 'rgba(255,255,255,0.14)',
    pillColor: 'rgba(236,72,153,0.9)',
    pillBorder: 'rgba(236,72,153,0.3)',
    btnColor: 'rgba(236,72,153,0.9)',
    btnBorder: 'rgba(236,72,153,0.4)',
  },
  college: {
    label: 'Institutions',
    headline: 'See where every student stands.',
    body: 'Progress, applications, and outcomes — across your whole school or firm.',
    outcomes: [
      'You know where every student stands, at any moment',
      'Answer any parent question with real data in front of you',
      'See where your graduates actually land, year over year',
    ],
    ctaText: 'Book a Demo →',
    cardBg: 'linear-gradient(135deg, rgba(230,168,23,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    topBorder: 'rgba(230,168,23,0.5)',
    shadowBase: 'rgba(230,168,23,0.08)',
    shadowHover: 'rgba(230,168,23,0.18)',
    borderHover: 'rgba(255,255,255,0.14)',
    pillColor: 'rgba(230,168,23,0.9)',
    pillBorder: 'rgba(230,168,23,0.3)',
    btnColor: 'rgba(230,168,23,0.9)',
    btnBorder: 'rgba(230,168,23,0.4)',
    comingSoon: true,
  },
}

// ──── PortalGlyph ─────────────────────────────────────────────
// Decorative product hint per card — built from divs + accent tokens only.
// Purely illustrative: no data, no numerals, aria-hidden.

const Bar: React.FC<{ w: string; color: string; dim?: boolean }> = ({ w, color, dim }) => (
  <div
    style={{
      height: 4,
      width: w,
      borderRadius: 999,
      background: color,
      opacity: dim ? 0.22 : 0.75,
    }}
  />
)

const PortalGlyph: React.FC<{ portal: PortalKey; accent: string }> = ({ portal, accent }) => {
  const track = 'rgba(255,255,255,0.07)'

  // Institutions — school-wide progress rows. Simplified to 3 rows with no
  // leading label stub: at the narrow card width, 4 labelled rows read as mush.
  if (portal === 'college') {
    return (
      <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 11, width: '100%' }}>
        {['72%', '48%', '86%'].map((w, i) => (
          <div key={i} style={{ height: 4, borderRadius: 999, background: track, position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                width: w,
                borderRadius: 999,
                background: accent,
                opacity: 0.85,
              }}
            />
          </div>
        ))}
      </div>
    )
  }

  // Counselors — caseload rows, one flagged
  if (portal === 'counselor') {
    return (
      <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: accent,
                opacity: i === 1 ? 1 : 0.35,
                flexShrink: 0,
              }}
            />
            <Bar w={i === 1 ? '78%' : '56%'} color={accent} dim={i !== 1} />
          </div>
        ))}
      </div>
    )
  }

  // Students — essay progress rows. Previously an empty outline chip plus two
  // fully dimmed bars (opacity 0.22), which read as washed-out beside the
  // counselor/institution glyphs. Now the same filled-track structure and
  // intensity as its siblings — only the hue differs.
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      {/* Filled accent chip — matches the solid weight of the counselor dot */}
      <div
        style={{
          alignSelf: 'flex-start',
          borderRadius: 999,
          background: accent,
          opacity: 0.85,
          height: 10,
          width: 44,
        }}
      />
      {['82%', '58%'].map((w, i) => (
        <div key={i} style={{ height: 4, borderRadius: 999, background: track, position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: w,
              borderRadius: 999,
              background: accent,
              opacity: 0.85,
            }}
          />
        </div>
      ))}
    </div>
  )
}

// ──── PortalCard ──────────────────────────────────────────────

interface PortalCardProps {
  portal: PortalKey
  floatDuration: number
  animDelay: number
  ctaHref: string
  anchorId: string
  isMobile: boolean
  highlighted: boolean
  /** A different card is spotlighted — this one recedes for the duration */
  dimmed: boolean
}

const PortalCard: React.FC<PortalCardProps> = ({ portal, floatDuration, animDelay, ctaHref, anchorId, isMobile, highlighted, dimmed }) => {
  const [hovered, setHovered] = useState(false)
  const prefersReduced = useReducedMotion()
  const data = PORTAL_DATA[portal]
  // Institutions is the primary buyer — weighted by accent strength, not width
  const isPrimary = portal === 'college'

  return (
    <motion.div
      id={anchorId}
      initial={prefersReduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: motionTokens.ease, delay: prefersReduced ? 0 : animDelay }}
      style={{ flex: isMobile ? 'none' : 1, width: isMobile ? '100%' : undefined }}
    >
      <motion.div
        // Only the secondary pair floats — the primary card stays anchored so it
        // reads as the stable focal point.
        animate={isMobile || prefersReduced || isPrimary ? {} : { y: [0, -10, 0] }}
        transition={{ duration: floatDuration, ease: 'easeInOut', repeat: Infinity }}
        style={{ height: '100%' }}
      >
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            background: data.cardBg,
            backdropFilter: 'blur(12px)',
            borderRadius: 20,
            // Accent border at low opacity by default, lifting on hover.
            // Highlighted (nav-requested) outranks hover — full-opacity accent.
            // The primary card carries a stronger resting accent than secondaries.
            border: `1px solid ${
              highlighted
                ? data.pillColor
                : hovered
                  ? data.borderHover
                  : isPrimary
                    ? data.topBorder
                    : data.pillBorder
            }`,
            borderTop: `1px solid ${highlighted ? data.pillColor : data.topBorder}`,
            padding: isMobile ? '28px 20px' : '34px 30px 32px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            // Equal-width row — cards share a height
            minHeight: isMobile ? 'auto' : 460,
            height: isMobile ? 'auto' : '100%',
            boxShadow: `0 0 ${highlighted ? 72 : hovered ? 60 : 40}px ${
              highlighted || hovered ? data.shadowHover : data.shadowBase
            }`,
            // Spotlight: the nav-target lifts and scales slightly; the other
            // cards recede. transform/opacity only — never affects document
            // flow, so there is no layout shift.
            transform: prefersReduced
              ? 'none'
              : highlighted
                ? 'translateY(-6px) scale(1.015)'
                : hovered
                  ? 'translateY(-4px)'
                  : 'translateY(0)',
            opacity: dimmed ? 0.45 : 1,
            transition: prefersReduced
              ? 'none'
              : 'box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease, opacity 0.25s ease',
            cursor: 'default',
          }}
        >
          {/* EARLY ACCESS badge */}
          {data.comingSoon && (
            <div
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                background: 'rgba(230,168,23,0.25)',
                border: '1px solid rgba(230,168,23,0.7)',
                color: '#e6a817',
                // Soft static glow — deliberately no pulse
                boxShadow: '0 0 16px rgba(230,168,23,0.28)',
                fontSize: 11,
                letterSpacing: '0.08em',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                textTransform: 'uppercase',
                padding: '4px 12px',
                borderRadius: 999,
              }}
            >
              EARLY ACCESS
            </div>
          )}

          {/* Portal pill */}
          <div
            style={{
              display: 'inline-flex',
              alignSelf: 'flex-start',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${data.pillBorder}`,
              borderRadius: 999,
              padding: '4px 12px',
              fontSize: 11,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              color: data.pillColor,
            }}
          >
            {data.label}
          </div>

          {/* Primary card splits into text | glyph. Secondary cards stack. */}
          <div
            style={{
              display: 'flex',
              // Every card: text left, mini-visual right
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 0 : 20,
              alignItems: isMobile ? 'stretch' : 'flex-start',
              flexGrow: 1,
            }}
          >
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>

          {/* Headline */}
          <p
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: isMobile ? 'clamp(24px, 6vw, 40px)' : 28,
              fontWeight: 700,
              color: colors.textPrimary,
              lineHeight: 1.15,
              marginTop: 24,
              marginBottom: 0,
            }}
          >
            {data.headline}
          </p>

          {/* Body */}
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 15,
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.7,
              marginTop: 16,
              marginBottom: 0,
            }}
          >
            {data.body}
          </p>

          {/* Outcome lines — what the buyer gets */}
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {data.outcomes.map((outcome) => (
              <div key={outcome} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {/* Accent marker dot — replaces the em-dash */}
                <span
                  aria-hidden="true"
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: data.pillColor,
                    flexShrink: 0,
                    marginTop: 7,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontFamily: 'Inter, sans-serif',
                    color: 'rgba(255,255,255,0.6)',
                    lineHeight: 1.6,
                  }}
                >
                  {outcome}
                </span>
              </div>
            ))}
          </div>

          {/* CTA — filled primary in this card's accent. Was a transparent
              outline pill that read as disabled. */}
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`landing-cta landing-cta--${portal}`}
            style={{
              marginTop: 32,
              width: 'fit-content',
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              textDecoration: 'none',
            }}
          >
            {data.ctaText}
          </a>

          </div>{/* end text column */}

          {/* Product glyph — shows the product instead of describing it.
              Primary: beside the text. Secondary: below it. Hidden on mobile
              and on narrow desktop where the wide card would squash. */}
          <div
            style={{
              flexShrink: 0,
              width: isMobile ? '100%' : 78,
              marginTop: isMobile ? 28 : 6,
              display: isMobile ? 'none' : 'block',
              opacity: 0.9,
            }}
          >
            <PortalGlyph portal={portal} accent={data.pillColor} />
          </div>

          </div>{/* end split */}
        </div>
      </motion.div>
    </motion.div>
  )
}

// ──── PortalShowcase ──────────────────────────────────────────

interface PortalShowcaseProps {
  highlightedPortal?: PortalKey | null
  onHighlightEnd?: () => void
}

function PortalShowcase({ highlightedPortal = null, onHighlightEnd }: PortalShowcaseProps) {
  const width = useWindowWidth()
  const isMobile = width < 768

  // Clear the nav highlight after ~2s
  useEffect(() => {
    if (!highlightedPortal || !onHighlightEnd) return
    const t = setTimeout(onHighlightEnd, HIGHLIGHT_MS)
    return () => clearTimeout(t)
  }, [highlightedPortal, onHighlightEnd])

  return (
    <section
      id="portal-showcase"
      style={{
        padding: isMobile ? '80px 20px' : '160px 24px',
        background: 'transparent',
        textAlign: 'center',
        // No scroll-margin: the nav centres #portal-composition rather than
        // start-aligning this section, so a top offset would only skew it.
      }}
    >
      {/* Composition wrapper — heading through tagline. Nav centers on THIS,
          not the section, whose 160px vertical padding would otherwise count
          toward centering and push the cards below the fold. */}
      <div id="portal-composition">

      {/* Eyebrow */}
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          margin: 0,
        }}
      >
        THE PLATFORM
      </p>

      {/* Headline */}
      <h2
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: isMobile ? 'clamp(32px, 8vw, 56px)' : 72,
          fontWeight: 700,
          color: colors.textPrimary,
          lineHeight: 1.05,
          marginTop: 16,
          marginBottom: isMobile ? 48 : 80,
        }}
      >
        Built for every side.
      </h2>

      {/* Cards — two tiers. Institutions leads full-width as the primary buyer;
          Counselors and Students sit below as a secondary pair. */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
          gap: isMobile ? 16 : 22,
          maxWidth: 1180,
          margin: '0 auto',
          textAlign: 'left',
        }}
      >
        {/* Three equal columns: Students — Counselors — Institutions.
            Institutions keeps visual weight through accent, not width. */}
        <PortalCard
          portal="student"
          floatDuration={4}
          animDelay={0}
          ctaHref={CALENDLY_URL}
          anchorId="portal-students"
          isMobile={isMobile}
          highlighted={highlightedPortal === 'student'}
          dimmed={!!highlightedPortal && highlightedPortal !== 'student'}
        />
        <PortalCard
          portal="counselor"
          floatDuration={4.8}
          animDelay={0.1}
          ctaHref={CALENDLY_URL}
          anchorId="portal-counselors"
          isMobile={isMobile}
          highlighted={highlightedPortal === 'counselor'}
          dimmed={!!highlightedPortal && highlightedPortal !== 'counselor'}
        />
        <PortalCard
          portal="college"
          floatDuration={5.4}
          animDelay={0.2}
          ctaHref={CALENDLY_URL}
          anchorId="portal-institutions"
          isMobile={isMobile}
          highlighted={highlightedPortal === 'college'}
          dimmed={!!highlightedPortal && highlightedPortal !== 'college'}
        />
      </div>

      {/* Bottom line */}
      <p
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: isMobile ? 24 : 32,
          fontWeight: 400,
          fontStyle: 'italic',
          color: 'rgba(255,255,255,0.45)',
          textAlign: 'center',
          marginTop: 64,
          marginBottom: 0,
        }}
      >
        One workspace. Every side of the application.
      </p>

      </div>{/* end composition wrapper */}
    </section>
  )
}

export default PortalShowcase
